/**
 * Sayfalı listelerin altbilgi metni.
 *
 * ## Neden ortak dosyada
 *
 * Aynı altbilgi on iki bileşende kopyalanmıştı ve **üç farklı** biçimde ayrışmıştı:
 *
 * ```
 * Showing 1 to 10 of {totalElements} entries                              (7 yerde)
 * Showing {pageNumber*10} to {(pageNumber+1)*10} of {totalElements}       (3 yerde)
 * Showing 1 to 10 of 50 entries                                           (3 yerde, sablon artigi)
 * ```
 *
 * Üçünün de hatası vardı:
 *
 * 1. **Sabit `1 to 10`** — sayfa değişse de aynı kalıyordu. 3. sayfada da "1 to 10"
 *    diyordu, yani kullanıcı için sayfalama çalışmıyormuş gibi görünüyordu.
 * 2. **Hesaplanan hali** `pageNumber * 10` ile başlıyordu: ilk sayfada
 *    **"Showing 0 to 10"**. Ve son sayfada taşıyordu — 24 kayıtlık bir listede
 *    3. sayfa "20 to 30" diyordu, oysa 24'te bitiyor.
 * 3. Üçü de `pageSize`'ı **10 varsayıyordu**; bileşenlerin bir kısmı farklı boyut
 *    kullanabiliyor.
 *
 * 2026-10-09'da tek yere alındı.
 */

/**
 * `"Showing 21 to 24 of 24 entries"` biçiminde metin üretir.
 *
 * @param pageNumber 0 TABANLI sayfa indeksi (Spring `Page.pageable.pageNumber`)
 * @param pageSize   sayfa başına kayıt
 * @param totalElements toplam kayıt; bilinmiyorsa `undefined`
 */
export function pageRangeText(
  pageNumber: number,
  pageSize: number,
  totalElements?: number | null
): string {
  const toplam = toSayi(totalElements)
  if (toplam === null) {
    // Toplam henüz gelmediyse sayı uydurmuyoruz. Eski kod bu durumda
    // "Showing 1 to 10 of  entries" gibi yarım bir metin basıyordu.
    return 'Kayıt sayısı bilinmiyor'
  }
  if (toplam === 0) {
    return 'Kayıt yok'
  }
  const boyut = pageSize > 0 ? pageSize : 10
  // 0 tabanlı indeksten 1 tabanlı gösterime: ilk sayfa "1 to ...", "0 to ..." değil.
  const baslangic = pageNumber * boyut + 1
  // Son sayfa eksik dolu olabilir; toplamı AŞMIYOR.
  const bitis = Math.min((pageNumber + 1) * boyut, toplam)
  return `Showing ${baslangic} to ${bitis} of ${toplam} entries`
}

/**
 * Sayıya çevirir; çevrilemiyorsa `null`.
 *
 * Metin de kabul ediyor çünkü bazı uçlar sayıyı string döndürüyor (P2-8'de büyük kısmı
 * düzeltildi ama hepsi değil). `parseInt` yerine `Number`: `parseInt("12abc")` sessizce
 * `12` döner, `Number("12abc")` ise `NaN`.
 */
function toSayi(deger: any): number | null {
  if (deger === null || deger === undefined || deger === '') {
    return null
  }
  const sayi = typeof deger === 'number' ? deger : Number(deger)
  return isNaN(sayi) ? null : sayi
}


/**
 * Geri gidilebilir mi?
 *
 * ## Neden paylaşılan bir yardımcı
 *
 * Sayfalama tuşlarının **davranışı** (handler'daki `if`) ile **görünümü** (tuşun devre
 * dışı çizilip çizilmemesi) iki ayrı yerde yazılırsa ayrışırlar. 2026-10-13'te
 * kullanıcı "dashboard karakutu listesi sağa sola gitmiyor" dedi: koşullar 9 Ekim'de
 * düzeltilmişti ve son sayfada ileri tuşu artık **doğru** şekilde hiçbir şey
 * yapmıyordu — ama tuş hâlâ tıklanabilir görünüyordu, yani düzeltilmiş davranış
 * bozukluk gibi hissediliyordu.
 *
 * İkisini tek fonksiyondan türetmek bu ayrışmayı imkânsız kılıyor.
 *
 * @param totalPages İkinci koşul (`pageNumber <= totalPages`) mantıksal olarak
 *        GEREKSİZ: `pageNumber` sunucunun döndürdüğü değerden geliyor, yani son
 *        sayfanın ötesinde olamıyor. Yine de korunuyor — canlı sistemde bu dilimin işi
 *        görünümü düzeltmek, davranışı değiştirmek değil. Ayrı bir adımda
 *        sadeleştirilebilir.
 */
export function canGoPrev(pageNumber: number, totalPages: number): boolean {
  return pageNumber >= 1 && pageNumber <= totalPages
}

/**
 * İleri gidilebilir mi?
 *
 * `pageNumber` 0 TABANLI, `totalPages` ise bir SAYI: son geçerli indeks
 * `totalPages - 1`. Eskiden koşul `pageNumber < totalPages` idi, yani son sayfadayken
 * bir ileri gidiliyor ve sunucu var olmayan sayfa için boş liste döndürüyordu.
 *
 * `totalPages` 0 ise (liste hiç yüklenmediyse) sonuç `false` — yüklenmemiş bir listede
 * sayfa değiştirmek anlamsız.
 */
export function canGoNext(pageNumber: number, totalPages: number): boolean {
  return pageNumber < totalPages - 1
}

/** Bootstrap `.page-item` sınıfı; gidilemiyorsa `disabled` ekliyor. */
export function pageItemClass(taban: string, gidilebilir: boolean): string {
  return gidilebilir ? taban : `${taban} disabled`
}
